import { useState, useEffect } from 'react';

import { LabellerFactViewer } from './fact-viewer';
import { useClusterFacts } from '@/api/mutations/useClusterFacts';
import { useGetFact } from '@/api/queries/useGetFact';
import { FilterOptions } from '@/constants/filterOptions';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent } from '@/new/components/shadcn/dialog';
import { Label } from '@/new/components/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/new/components/shadcn/select';
import { Textarea } from '@/new/components/shadcn/textarea';
import { Classification, Relevance } from '@/types';

interface ClusterFactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  matterId: string;
  subsetId: string;
  selectedFactIds: Set<string>;
  onSuccess: () => void;
}

export const ClusterFactsModal = ({
  isOpen,
  onClose,
  matterId,
  selectedFactIds,
  onSuccess,
}: ClusterFactsModalProps) => {
  const factIdsArray = Array.from(selectedFactIds);
  const totalCount = factIdsArray.length;

  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const currentFactId = factIdsArray[currentFactIndex] ?? '';
  const { data: currentFact, isLoading, isError } = useGetFact(matterId, currentFactId);

  useEffect(() => {
    if (isOpen) {
      setCurrentFactIndex(0);
    }
  }, [isOpen]);

  const handlePrevFact = () => setCurrentFactIndex((prev) => Math.max(0, prev - 1));
  const handleNextFact = () => setCurrentFactIndex((prev) => Math.min(totalCount - 1, prev + 1));

  const [description, setDescription] = useState('');
  const [author, setAuthor] = useState('');
  const [factDateText, setFactDateText] = useState('');
  const [significance, setSignificance] = useState('');
  const [topic, setTopic] = useState('');
  const [relevance, setRelevance] = useState<Relevance>(Relevance.Relevant);
  const [classification, setClassification] = useState<Classification>(Classification.Primary);

  useEffect(() => {
    if (isOpen) {
      setDescription('');
      setAuthor('');
      setFactDateText('');
      setSignificance('');
      setTopic('');
      setRelevance(Relevance.Relevant);
      setClassification(Classification.Primary);
    }
  }, [isOpen]);

  const { mutate: clusterFacts, isLoading: isClustering } = useClusterFacts(matterId, {
    onSuccess: () => {
      onSuccess();
      onClose();
    },
  });

  const handleCluster = () => {
    if (!description.trim()) {
      return;
    }

    clusterFacts({
      factIds: factIdsArray,
      description,
      author: author || undefined,
      factDateText: factDateText || undefined,
      significance: significance || undefined,
      topic: topic || undefined,
      relevance,
      classification,
      issues: [],
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[calc(100vw-4rem)]">
        <div className="flex max-h-[70vh]">
          <LabellerFactViewer
            currentFact={currentFact}
            currentFactIndex={currentFactIndex}
            total={totalCount}
            isLoading={isLoading}
            isError={isError}
            onPrevious={handlePrevFact}
            onNext={handleNextFact}
          />

          <div className="w-1/2 overflow-y-auto p-6">
            <h3 className="mb-4 text-lg font-semibold">New Clustered Fact</h3>
            <div className="mb-4 flex gap-2.5 rounded-md border border-gray-300 bg-gray-50 px-4 py-3 text-gray-600">
              <Icon className="lucide--info mt-0.75" />
              <p>
                Clustering deletes the original facts in Postgres and creates a new fact in their place. The new fact
                may take up to ~1 minute to appear in the facts table while OpenSearch catches up.
              </p>
            </div>

            <div className="space-y-3">
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter the consolidated fact description"
                  className="mt-1"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="author">Author</Label>
                  <input
                    id="author"
                    type="text"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    placeholder="Enter author"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <Label htmlFor="factDateText">Date</Label>
                  <input
                    id="factDateText"
                    type="text"
                    value={factDateText}
                    onChange={(e) => setFactDateText(e.target.value)}
                    placeholder="Enter date"
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="topic">Topic</Label>
                <input
                  id="topic"
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="Enter topic"
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <Label htmlFor="significance">Significance</Label>
                <Textarea
                  id="significance"
                  value={significance}
                  onChange={(e) => setSignificance(e.target.value)}
                  placeholder="Enter significance"
                  className="mt-1"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="relevance">Relevance</Label>
                  <Select value={relevance} onValueChange={(value) => setRelevance(value as Relevance)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FilterOptions.relevance.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="classification">Classification</Label>
                  <Select value={classification} onValueChange={(value) => setClassification(value as Classification)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FilterOptions.classification.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
          <Button variant="outline" onClick={onClose} disabled={isClustering}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleCluster} disabled={!description.trim() || isClustering}>
            {isClustering ? 'Clustering...' : 'Cluster Facts'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
